import { AccessToken, IdToken, RefreshToken, SSOTokens, Token, TokenClaims, TokenType } from './token-manager.types';
import { jwtDecode } from 'jwt-decode';
import { V2TokenResponse } from '../router/v2.router';

type TokenObjectOpts<TType extends TokenType> = TType extends 'access'
  ? {
      type: 'access';
      issuer: string;
      scopes: Array<string>;
      tokenUse: string;
    }
  : TType extends 'id'
    ? { scopes: Array<string>; issuer: string; type: 'id' }
    : TType extends 'refresh'
      ? { type: 'refresh'; issuer: string }
      : never;

export function createTokenObjectFromJwt(token: string, opts: TokenObjectOpts<'access'>): AccessToken;
export function createTokenObjectFromJwt(token: string, opts: TokenObjectOpts<'id'>): IdToken;
export function createTokenObjectFromJwt(token: string, opts: TokenObjectOpts<'refresh'>): RefreshToken;
export function createTokenObjectFromJwt<TType extends TokenType>(token: string, opts: TokenObjectOpts<TType>): Token {
  switch (opts.type) {
    case 'access': {
      const claims = jwtDecode<TokenClaims>(token);
      return {
        issuer: claims.iss ?? opts.issuer,
        token: token,
        claims,
        expiresAt: expiresAt(Number(claims.exp)),
        tokenUse: opts.tokenUse,
        scopes: opts.scopes,
        tokenType: 'access',
      } satisfies AccessToken;
    }
    case 'id': {
      const claims = jwtDecode<TokenClaims>(token);
      return {
        issuer: claims.iss ?? opts.issuer,
        token: token,
        claims,
        expiresAt: expiresAt(Number(claims.exp)),
        scopes: opts.scopes,
        tokenType: 'id',
      } satisfies IdToken;
    }
    case 'refresh':
      return {
        issuer: opts.issuer,
        token: token,
        tokenType: 'refresh',
      } satisfies RefreshToken;
  }
}

function expiresAt(exp: number) {
  return exp * 1000;
}

export function transformTokenResponse(tokenResponse: V2TokenResponse, issuer: string): SSOTokens {
  const scopes = tokenResponse.scope.split(' ');
  return {
    accessToken: createTokenObjectFromJwt(tokenResponse.access_token, {
      type: 'access',
      issuer,
      scopes,
      tokenUse: tokenResponse.token_type,
    }),
    idToken: createTokenObjectFromJwt(tokenResponse.id_token, {
      type: 'id',
      issuer,
      scopes,
    }),
    refreshToken: createTokenObjectFromJwt(tokenResponse.refresh_token, {
      type: 'refresh',
      issuer,
    }),
  };
}
